import { SCALE } from "@/domains/tokens/static-tokens"

import type { OrchestratedQueryStore } from "@/domains/library"
import type { LockRewardRepository, LockRewardsOnChainTransport } from "@/domains/lock-rewards"
import type { ITokenLookup } from "@/domains/tokens/types"

import type { ILockRefreshStrategy } from "./interfaces"
import type { LockStoreDAO } from "./types"
import { makeDefaultLockStoreDAO } from "."
import { LockStore } from "./lock-store"

export class LockStoreFactory {
  constructor(
    private lockRewardsOnChainTransport: LockRewardsOnChainTransport,
    private lockRewardRepository: LockRewardRepository,
    private tokenLookup: ITokenLookup,
    private orchestratedQueryStore: OrchestratedQueryStore,
    private lockRefreshStrategy?: ILockRefreshStrategy | null,
    private queryNamePrefix?: string,
  ) {}

  /**
   * Create a LockStore from a DAO
   */
  createFromDao(dao: LockStoreDAO): LockStore {
    return new LockStore(
      dao,
      this.lockRewardsOnChainTransport,
      this.lockRewardRepository,
      this.tokenLookup,
      this.orchestratedQueryStore,
      this.lockRefreshStrategy,
      this.queryNamePrefix,
    )
  }

  /**
   * Create a default LockStore with default DAO
   */
  createDefault(): LockStore {
    const defaultDao = makeDefaultLockStoreDAO()
    return this.createFromDao(defaultDao)
  }

  /**
   * Create a LockStore with specific NFT data
   */
  createFromNft(nft: { id: number; lockAmount: number; lockEnds: number; lockValue: number }): LockStore {
    const dao: LockStoreDAO = makeDefaultLockStoreDAO()
    dao.id = nft.id
    dao.lockAmount = nft.lockAmount
    // Compute lockAmountRaw from lockAmount (loses precision, but needed for backward compatibility)
    dao.lockAmountRaw = SCALE.parseUnits(nft.lockAmount.toString())
    dao.lockEnds = nft.lockEnds
    dao.lockValue = nft.lockValue
    return this.createFromDao(dao)
  }

  /**
   * Create a merged LockStore from two NFTs
   */
  createMerged(
    nft1: { id: number; lockAmount: number; lockEnds: number; lockValue: number },
    nft2: { id: number; lockAmount: number; lockEnds: number; lockValue: number },
  ): LockStore {
    const dao: LockStoreDAO = makeDefaultLockStoreDAO()

    // Merge logic
    dao.id = nft1.id // Keep first NFT's ID
    dao.lockAmount = Number(nft1.lockAmount) + Number(nft2.lockAmount)
    // Compute lockAmountRaw from merged lockAmount (loses precision, but needed for backward compatibility)
    dao.lockAmountRaw = SCALE.parseUnits(dao.lockAmount.toString())
    dao.lockEnds = Math.max(nft1.lockEnds, nft2.lockEnds)
    dao.lockValue = Number(nft1.lockValue) + Number(nft2.lockValue)

    return this.createFromDao(dao)
  }
}
